import { useState, useEffect, useRef, useCallback } from 'react'
import { useConversationsStore } from '@/stores/conversationsStore'
import { cn } from '@/utils/cn'
import {
  Plus, X, MessageCircle, Pencil, Trash2, Check, GripVertical,
  Share2, FolderPlus, Folder, FolderOpen, ChevronRight, FolderInput, Search
} from 'lucide-react'
import { assistantApi } from '@/lib/api'
import { ConversationItemSkeleton, FolderSkeleton } from '@/components/ui/Skeleton'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

interface ChatSidebarProps {
  isOpen: boolean
  onClose: () => void
  onWidthChange?: (width: number) => void
}

const FOLDER_COLORS = [
  '#a3de5d', '#f87000', '#0A332F', '#3b82f6',
  '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
]

export function ChatSidebar({ isOpen, onClose, onWidthChange }: ChatSidebarProps) {
  const {
    conversations, folders, loading,
    currentConversationId,
    deleteConversation,
    setCurrentConversation, updateConversationTitle,
    loadFolders, createFolder, updateFolder, deleteFolder, moveToFolder,
  } = useConversationsStore()

  // Search state with debounce
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 250)
    return () => clearTimeout(t)
  }, [searchQuery])

  const filteredConversations = debouncedQuery.trim()
    ? conversations.filter(c => c.title.toLowerCase().includes(debouncedQuery.toLowerCase()))
    : conversations

  // Conversation state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const [sharingId, setSharingId] = useState<string | null>(null)
  const [movingId, setMovingId] = useState<string | null>(null)

  // Confirm dialog state
  const [confirmState, setConfirmState] = useState<{
    open: boolean
    message: string
    onConfirm: () => void
  }>({ open: false, message: '', onConfirm: () => { } })

  const closeConfirm = useCallback(() => setConfirmState(s => ({ ...s, open: false })), [])

  // Folder state
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set())
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null)
  const [editingFolderName, setEditingFolderName] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [newFolderColor, setNewFolderColor] = useState(FOLDER_COLORS[0])

  // Drag-and-drop (no setState during drag to avoid re-renders cancelling the operation)

  // Init folders as collapsed (including new ones)
  useEffect(() => {
    setCollapsedFolders(prev => {
      const newCollapsed = new Set(prev)
      folders.forEach(f => newCollapsed.add(f.id))
      return newCollapsed
    })
  }, [folders.length])

  // Resize state
  const [sidebarWidth, setSidebarWidth] = useState(288)
  const [isResizing, setIsResizing] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadFolders()
  }, [loadFolders])

  // --- Conversation handlers ---

  const handleNewChat = () => { setCurrentConversation(null); onClose() }
  const handleSelectConversation = (id: string) => { setCurrentConversation(id); onClose() }

  const handleStartEdit = (id: string, title: string) => { setEditingId(id); setEditingTitle(title) }
  const handleSaveEdit = () => {
    if (editingId && editingTitle.trim()) updateConversationTitle(editingId, editingTitle.trim())
    setEditingId(null); setEditingTitle('')
  }
  const handleCancelEdit = () => { setEditingId(null); setEditingTitle('') }

  const handleShare = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSharingId(id)
    try {
      const res = await assistantApi.post(`/assistant/conversations/${id}/share`)
      const url = `${window.location.origin}/shared/${res.data.share_token}`
      if (navigator.share) await navigator.share({ title: 'Conversa RegenerAI', url }).catch(() => { })
      else await navigator.clipboard.writeText(url)
    } catch (err) {
      console.error('Error generating share link:', err)
    } finally {
      setTimeout(() => setSharingId(null), 2000)
    }
  }

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setConfirmState({
      open: true,
      message: 'Tem certeza que deseja excluir esta conversa?',
      onConfirm: () => { deleteConversation(id); closeConfirm() },
    })
  }

  const handleMoveToFolder = (convId: string, folderId: string | null, e: React.MouseEvent) => {
    e.stopPropagation()
    moveToFolder(convId, folderId)
    setMovingId(null)
  }

  // --- Folder handlers ---

  const toggleFolder = (id: string) => {
    setCollapsedFolders(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return
    await createFolder(newFolderName.trim(), newFolderColor)
    setNewFolderName(''); setCreatingFolder(false)
  }

  const handleStartFolderEdit = (id: string, name: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingFolderId(id); setEditingFolderName(name)
  }

  const handleSaveFolderEdit = () => {
    if (editingFolderId && editingFolderName.trim())
      updateFolder(editingFolderId, editingFolderName.trim())
    setEditingFolderId(null); setEditingFolderName('')
  }

  const handleDeleteFolder = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setConfirmState({
      open: true,
      message: 'Excluir pasta? As conversas ficarão sem pasta.',
      onConfirm: () => { deleteFolder(id); closeConfirm() },
    })
  }

  // --- Drag-and-drop handlers ---

  const handleDragStart = (convId: string, e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', convId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnd = () => { }

  const handleFolderDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    e.currentTarget.classList.add('drag-over-folder')
  }

  const handleFolderDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('drag-over-folder')
  }

  const handleFolderDrop = (folderId: string, e: React.DragEvent) => {
    e.preventDefault()
    e.currentTarget.classList.remove('drag-over-folder')
    const convId = e.dataTransfer.getData('text/plain')
    if (convId) moveToFolder(convId, folderId)
  }

  const handleUnfiledDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    e.currentTarget.classList.add('drag-over-folder')
  }

  const handleUnfiledDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.currentTarget.classList.remove('drag-over-folder')
    const convId = e.dataTransfer.getData('text/plain')
    if (convId) moveToFolder(convId, null)
  }

  // --- Resize ---

  const handleMouseDown = (e: React.MouseEvent) => { e.preventDefault(); setIsResizing(true) }

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isResizing) return
      const w = e.clientX
      if (w >= 240 && w <= 600) { setSidebarWidth(w); onWidthChange?.(w) }
    }
    const onUp = () => setIsResizing(false)
    if (isResizing) {
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  // --- Render helpers ---

  const ConversationItem = ({ conv }: { conv: typeof conversations[0] }) => (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      draggable
      onDragStart={e => handleDragStart(conv.id, e)}
      onDragEnd={handleDragEnd}
      className={cn(
        "group relative p-3 rounded-xl transition-all duration-200 select-none",
        currentConversationId === conv.id
          ? "bg-brand-primary-light border border-brand-accent/30"
          : "hover:bg-brand-surface-alt"
      )}
      onClick={() => handleSelectConversation(conv.id)}
    >
      {editingId === conv.id ? (
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <input
            type="text" value={editingTitle} autoFocus
            onChange={e => setEditingTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit() }}
            className="flex-1 px-2 py-1 text-sm input-field"
          />
          <button onClick={handleSaveEdit} className="p-1 text-brand-accent hover:bg-brand-surface-alt rounded-lg"><Check size={16} /></button>
          <button onClick={handleCancelEdit} className="p-1 text-brand-text-muted hover:bg-brand-surface-alt rounded-lg"><X size={16} /></button>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-brand-text truncate">{conv.title}</h3>
                <p className="text-xs text-brand-text-muted mt-0.5">{conv.messages.length} mensagens</p>
              </div>
            </div>
            <div className="flex items-center gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
              {/* Move to folder */}
              <div className="relative">
                <button
                  onClick={e => { e.stopPropagation(); setMovingId(movingId === conv.id ? null : conv.id) }}
                  className="p-1.5 text-brand-text-muted hover:text-brand-primary hover:bg-brand-primary-light rounded-lg transition-colors"
                  title="Mover para pasta"
                >
                  <FolderInput size={15} />
                </button>
                {movingId === conv.id && (
                  <div
                    className="absolute right-0 top-full mt-1 w-44 bg-brand-surface border border-brand-border rounded-xl shadow-lg z-20 py-1 text-sm"
                    onClick={e => e.stopPropagation()}
                  >
                    {conv.folderId && (
                      <button
                        onClick={e => handleMoveToFolder(conv.id, null, e)}
                        className="w-full text-left px-3 py-2 hover:bg-brand-surface-alt text-brand-text-muted flex items-center gap-2"
                      >
                        <X size={13} /> Remover da pasta
                      </button>
                    )}
                    {folders.map(f => (
                      <button
                        key={f.id}
                        onClick={e => handleMoveToFolder(conv.id, f.id, e)}
                        className={cn(
                          "w-full text-left px-3 py-2 hover:bg-brand-surface-alt text-brand-text flex items-center gap-2",
                          conv.folderId === f.id && "font-semibold"
                        )}
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: f.color }} />
                        <span className="truncate">{f.name}</span>
                        {conv.folderId === f.id && <Check size={12} className="ml-auto" />}
                      </button>
                    ))}
                    {folders.length === 0 && (
                      <p className="px-3 py-2 text-brand-text-muted text-xs">Nenhuma pasta criada</p>
                    )}
                  </div>
                )}
              </div>
              <button onClick={() => handleStartEdit(conv.id, conv.title)} className="p-1.5 text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-alt rounded-lg transition-colors" title="Renomear">
                <Pencil size={15} />
              </button>
              <button onClick={e => handleShare(conv.id, e)} className="p-1.5 rounded-lg transition-all duration-200" title={sharingId === conv.id ? 'Link copiado!' : 'Compartilhar conversa'}>
                {sharingId === conv.id ? (
                  <span className="flex items-center gap-1 text-xs text-brand-primary font-medium animate-in fade-in zoom-in duration-300">
                    <Check size={14} /> Copiado!
                  </span>
                ) : (
                  <Share2 size={15} className="text-brand-text-muted hover:text-brand-primary hover:bg-brand-primary-light rounded" />
                )}
              </button>
              <button onClick={e => handleDelete(conv.id, e)} className="p-1.5 text-brand-text-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-colors" title="Excluir">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
          <p className="text-[10px] text-brand-text-muted mt-1 opacity-60">
            {new Date(conv.updatedAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
          </p>
        </>
      )}
    </div>
  )

  const unfiledConversations = conversations.filter(c => !c.folderId)

  return (
    <div
      ref={sidebarRef}
      className={cn(
        "fixed inset-y-0 left-0 bg-brand-surface border-r border-brand-border z-50 transform",
        "lg:relative lg:inset-0 lg:transform-none",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        !isResizing && "transition-transform duration-300 ease-in-out"
      )}
      style={{ width: `${sidebarWidth}px` }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b border-brand-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-brand-text">Conversas</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCreatingFolder(v => !v)}
                className="p-1.5 rounded-lg hover:bg-brand-surface-alt text-brand-text-secondary transition-colors"
                title="Nova pasta"
              >
                <FolderPlus size={18} />
              </button>
              <button
                onClick={onClose}
                className="lg:hidden p-1.5 rounded-lg hover:bg-brand-surface-alt text-brand-text-secondary transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <button onClick={handleNewChat} className="w-full flex items-center justify-center gap-2 btn-primary">
            <Plus size={20} />
            Nova conversa
          </button>

          {/* Search */}
          {conversations.length > 3 && (
            <div className="relative mt-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-muted pointer-events-none" />
              <input
                type="text"
                placeholder="Buscar conversas..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs input-field"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-text-muted hover:text-brand-text"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}

          {/* New folder form */}
          {creatingFolder && (
            <div className="mt-3 space-y-2" onClick={e => e.stopPropagation()}>
              <input
                type="text" placeholder="Nome da pasta" autoFocus
                value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setCreatingFolder(false) }}
                className="w-full px-3 py-1.5 text-sm input-field"
              />
              <div className="flex items-center gap-1.5">
                {FOLDER_COLORS.map(c => (
                  <button key={c} onClick={() => setNewFolderColor(c)}
                    className={cn("w-5 h-5 rounded-full transition-transform", newFolderColor === c && "scale-125 ring-2 ring-offset-1 ring-brand-primary")}
                    style={{ background: c }}
                  />
                ))}
                <button onClick={handleCreateFolder} className="ml-auto p-1 text-brand-accent hover:bg-brand-surface-alt rounded-lg"><Check size={16} /></button>
                <button onClick={() => setCreatingFolder(false)} className="p-1 text-brand-text-muted hover:bg-brand-surface-alt rounded-lg"><X size={16} /></button>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 scrollbar-thin space-y-1" onClick={() => setMovingId(null)}>
          {loading ? (
            <div className="space-y-3 p-2">
              <FolderSkeleton />
              <ConversationItemSkeleton />
              <ConversationItemSkeleton />
              <ConversationItemSkeleton />
            </div>
          ) : conversations.length === 0 && folders.length === 0 ? (
            <div className="text-center py-10 px-4 animate-in fade-in duration-500">
              <div className="w-16 h-16 bg-gradient-to-br from-brand-primary-light to-brand-surface-alt rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                <MessageCircle size={28} className="text-brand-primary" />
              </div>
              <h3 className="text-brand-text font-medium text-sm mb-1">Nenhuma conversa ainda</h3>
              <p className="text-brand-text-muted text-xs leading-relaxed">Clique no botão verde acima para iniciar sua primeira conversa com a IA da RegenerAI</p>
            </div>
          ) : (
            <>
              {/* Search results mode */}
              {debouncedQuery.trim() && (
                <div className="mb-2">
                  <p className="text-xs text-brand-text-muted px-1 mb-1">
                    {filteredConversations.length === 0
                      ? 'Nenhum resultado'
                      : `${filteredConversations.length} resultado${filteredConversations.length !== 1 ? 's' : ''}`}
                  </p>
                  {filteredConversations.map(c => <ConversationItem key={c.id} conv={c} />)}
                </div>
              )}

              {/* Folders (sorted by most recent) — hidden during search */}
              {!debouncedQuery.trim() && [...folders].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(folder => {
                const folderConvs = conversations.filter(c => c.folderId === folder.id)
                const isCollapsed = collapsedFolders.has(folder.id)
                return (
                  <div key={folder.id} className="rounded-lg border border-brand-border/50">
                    <div
                      className="group flex items-center gap-2 px-2 py-1.5 cursor-pointer transition-colors bg-brand-surface-alt hover:bg-brand-surface-alt/80 border-l-2 [&.drag-over-folder]:bg-brand-accent/20 [&.drag-over-folder]:border-brand-accent/40 [&.drag-over-folder]:border-dashed"
                      style={{ borderLeftColor: folder.color }}
                      onClick={() => toggleFolder(folder.id)}
                      onDragOver={handleFolderDragOver}
                      onDragLeave={handleFolderDragLeave}
                      onDrop={e => handleFolderDrop(folder.id, e)}
                    >
                      <ChevronRight size={14} className={cn("text-brand-text-muted flex-shrink-0 transition-transform", !isCollapsed && "rotate-90")} />
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: folder.color }} />
                      {editingFolderId === folder.id ? (
                        <input
                          type="text" value={editingFolderName} autoFocus
                          className="flex-1 px-1 py-0 text-xs input-field"
                          onChange={e => setEditingFolderName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveFolderEdit(); if (e.key === 'Escape') setEditingFolderId(null) }}
                          onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <span className="flex-1 text-xs font-semibold text-brand-text truncate">
                          {folder.name}
                          <span className="ml-1 text-brand-text-muted font-normal">({folderConvs.length})</span>
                        </span>
                      )}
                      <div className="flex items-center gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button onClick={e => handleStartFolderEdit(folder.id, folder.name, e)} className="p-1 text-brand-text-muted hover:text-brand-text rounded transition-colors" title="Renomear pasta">
                          <Pencil size={12} />
                        </button>
                        <button onClick={e => handleDeleteFolder(folder.id, e)} className="p-1 text-brand-text-muted hover:text-red-500 rounded transition-colors" title="Excluir pasta">
                          <Trash2 size={12} />
                        </button>
                      </div>
                      {isCollapsed
                        ? <Folder size={14} className="flex-shrink-0 text-brand-text-muted" />
                        : <FolderOpen size={14} className="flex-shrink-0 text-brand-text-muted" />}
                    </div>
                    {!isCollapsed && (
                      <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 pl-2 pb-1" style={{ borderColor: folder.color + '55' }}>
                        {folderConvs.length === 0 ? (
                          <div className="px-2 py-3 text-center">
                            <p className="text-xs text-brand-text-muted/70 italic">Arraste conversas para cá</p>
                          </div>
                        ) : folderConvs.map(c => <ConversationItem key={c.id} conv={c} />)}
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Unfiled conversations — hidden during search */}
              {!debouncedQuery.trim() && (unfiledConversations.length > 0 || folders.length > 0) && (
                <div
                  onDragOver={handleUnfiledDragOver}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={handleUnfiledDrop}
                  className="rounded-lg transition-colors [&.drag-over-folder]:bg-brand-accent/10 [&.drag-over-folder]:border [&.drag-over-folder]:border-brand-accent/30 [&.drag-over-folder]:border-dashed"
                >
                  {folders.length > 0 && (
                    <p className="text-[10px] uppercase font-semibold tracking-wider px-2 pt-2 text-brand-text-muted">Sem pasta</p>
                  )}
                  <div className="space-y-0.5">
                    {unfiledConversations.map(c => <ConversationItem key={c.id} conv={c} />)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-brand-border">
          <div className="text-xs text-brand-text-muted text-center">
            {conversations.length} conversa{conversations.length !== 1 ? 's' : ''}
            {folders.length > 0 && ` · ${folders.length} pasta${folders.length !== 1 ? 's' : ''}`}
          </div>
        </div>
      </div>

      {/* Resize Handle */}
      <div
        className={cn(
          "hidden lg:block absolute top-0 right-0 w-1 h-full cursor-col-resize group hover:bg-brand-accent/20 transition-colors",
          isResizing && "bg-brand-accent/30"
        )}
        onMouseDown={handleMouseDown}
      >
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-brand-surface border border-brand-border rounded-lg p-1 shadow-lg">
            <GripVertical size={16} className="text-brand-text-muted" />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmState.open}
        title="Excluir"
        message={confirmState.message}
        confirmLabel="Excluir"
        variant="danger"
        onConfirm={confirmState.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  )
}